import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useTranslation } from 'react-i18next';
import { Interview } from '@/types/interview';
import AISummary from './AISummary';
import Innspill from './Innspill';
import ChatHistory from './ChatHistory';
import QuizSection from '@/components/quiz/QuizSection';
import { CommentSection } from '@/components/social/CommentSection';
import { DebateSection } from '@/components/social/DebateSection';
import { PollSection } from '@/components/poll/PollSection';
import { useAiChat } from '@/hooks/interview/useAiChat';

interface InterviewExpandedProps {
  interview: Interview;
}

export const InterviewExpanded: React.FC<InterviewExpandedProps> = ({ interview }) => {
  const { t } = useTranslation();
  const { aiChat, handleAiQuestion, handleAiResponse } = useAiChat();
  return (
    <Card className="mt-4 md:border md:shadow-sm border-0 shadow-none">
      <CardContent className="pt-4 md:pt-6 px-0 md:px-6">
        <Tabs defaultValue="ai-summary" className="w-full">
          <TabsList className="grid grid-cols-2 md:flex md:flex-row w-full gap-2 mb-20 md:mb-4 p-0 bg-transparent px-4 md:px-0">
            <TabsTrigger value="ai-summary" className="text-xs md:text-sm md:flex-1 bg-muted border border-border">{t('tabs.summary')}</TabsTrigger>
            <TabsTrigger value="innspill" className="text-xs md:text-sm md:flex-1 bg-muted border border-border">{t('tabs.input')}</TabsTrigger>
            <TabsTrigger value="debates" className="text-xs md:text-sm md:flex-1 bg-muted border border-border">{t('tabs.debates')}</TabsTrigger>
            <TabsTrigger value="comments" className="text-xs md:text-sm md:flex-1 bg-muted border border-border">{t('tabs.aiChat')}</TabsTrigger>
            <TabsTrigger value="poll" className="text-xs md:text-sm md:flex-1 bg-muted border border-border">{t('tabs.poll')}</TabsTrigger>
            <TabsTrigger value="quiz" className="text-xs md:text-sm md:flex-1 bg-muted border border-border">{t('tabs.quiz')}</TabsTrigger>
          </TabsList>
          
          <TabsContent value="ai-summary" className="mt-4 px-4 md:px-0">
            <AISummary 
              aiSummary={interview.aiSummary || []}
              aiResponse=""
              summaryStatus={interview.summary_status as any}
              summaryError={interview.summary_error}
              context={interview.context}
              question={interview.question}
              answer={interview.answer}
              intervieweeName={interview.intervieweeName}
              title={interview.title}
              company={interview.company}
              interviewId={interview.id}
            />
          </TabsContent>
          
          <TabsContent value="innspill" className="mt-4 px-4 md:px-0">
            <Innspill interviewId={interview.id} />
          </TabsContent>
          
          <TabsContent value="debates" className="mt-4 px-4 md:px-0">
            <DebateSection interviewId={interview.id} />
          </TabsContent>
          
          <TabsContent value="comments" className="mt-4 px-4 md:px-0">
            <ChatHistory 
              onSubmit={handleAiQuestion}
              onResponse={handleAiResponse}
              context={`
ARTIKKEL: ${interview.title}

EKSPERT SOM BLIR INTERVJUET: ${interview.intervieweeName} ${interview.company ? `fra ${interview.company}` : ''}

INTERVJUSPØRSMÅL: ${interview.question}
EKSPERTENS SVAR: ${interview.answer}

ARTIKKELINNHOLD: ${interview.context}

AI-OPPSUMMERING: ${Array.isArray(interview.aiSummary) ? interview.aiSummary.join('\n') : interview.aiSummary || ''}

${interview.aiResponse ? `TIDLIGERE SVAR FRA GEMENIKI: ${interview.aiResponse}` : ''}
              `.trim()}
              chat={aiChat}
            />
          </TabsContent>
          
          <TabsContent value="poll" className="mt-4 px-4 md:px-0">
            <PollSection interviewId={interview.id} />
          </TabsContent>
          
          <TabsContent value="quiz" className="mt-4 px-4 md:px-0">
            <QuizSection interviewId={interview.id} />
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};
